import React, { useEffect, useRef } from 'react';

import { Flex, PopoverTrigger, Popover, PopoverContent, useDisclosure, useOutsideClick, Box } from '@chakra-ui/react';
import { MenuList, Paper } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { onOpenNotificationsActionApi } from 'api/notifications';
import NoDataComponent from 'components/NoDataComponent';
import { MenuListContent } from 'components/header/UserSettingsDropdownRabbit';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { HideScrollbarCSS } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ChevronRight, Notifications } from 'assets/assetsV1/iconComponents';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString } from 'constants/helper';
import { sendNotificationPanelOpenedEvent } from 'constants/mixpanelHelper';

import { getNotificationsCountAndData } from 'services/notifications';
import { showTaskDetails } from 'services/taskDetails';

import { NotificationBadge } from 'uiToolkitV1/NotificationBadge';
import { TextBodyLarge } from 'uiToolkitV1/Typography';

import NoNotificationsView from './NoNotificationsView';
import NotificationComponent from './NotificationComponent';
import { useNotifications } from './context';

const limit = 10;

const NotificationsPanel = ({ maxWidth = '390px', closeMenu, isHovered = false, onNotificationClick = () => {} }) => {
  const {
    state: { notificationsCount, notificationsMeta: meta, notificationsData },
    addToEndOfNotifications,
    updateNotificationsMeta,
    setNotificationsCount,
    updateNotificationClickedStatus,
  } = useNotifications();

  const history = useHistory();

  // const [anchorEl, setAnchorEl] = useState(null);

  const { isOpen, onToggle, onClose } = useDisclosure();

  const panelRef = useRef();

  const buttonRef = useRef();

  useOutsideClick({
    ref: panelRef,
    handler: (event) => {
      // doing this to make on toggle work
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return;
      }
      onClose();
    },
  });

  const handleClick = (event) => {
    // setAnchorEl(anchorEl ? null : event.currentTarget);
    onToggle();
    if (notificationsCount > 0) {
      sendNotificationPanelOpenedEvent();

      onOpenNotificationsActionApi()
        .then((res) => setNotificationsCount(0))
        .catch((err) => console.error(err));
    }
  };

  useEffect(() => {
    if (isHovered && !isOpen) {
      handleClick();
      return;
    }

    if (!isHovered && isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered]);

  // const handleClose = action => {
  //   if (typeof action === 'function') action();
  //   setAnchorEl(null);
  // };

  const fetchPaginatedNotifications = (page) => {
    page -= 1;
    const offset = page === 0 ? 0 : page * limit;
    getNotificationsCountAndData({ callType: 'PAGINATION', limit, offset })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          addToEndOfNotifications(res.data);
        }
        if (res.data && res.data.length < limit) updateNotificationsMeta({ hasMore: false });
      })
      .catch((err) => {
        console.error('Error fetching notifications and count : ', err);
      });
  };

  function updateNotificationClickStatus(notificationId) {
    updateNotificationClickedStatus(notificationId);
  }
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { keyResultsName } = useOrganisationSettings();

  const dispatch = useDispatch();
  const showDetails = (taskIds, type) =>
    dispatch(showTaskDetails({ taskIds, employee: loggedInEmployee, type, history }));

  return (
    <Popover
      closeOnBlur={false}
      returnFocusOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      placement={checkNewNavEnabled() ? 'right-end' : 'left-start'}
      trigger="click"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: checkNewNavEnabled() ? [174, 95] : [0, 90],
          },
        },
      ]}
      isLazy
    >
      <>
        <PopoverTrigger>
          <Flex
            onClick={handleClick}
            alignItems="center"
            sx={{
              ':hover': {
                svg: {
                  color: '#333333',
                },
                background: checkNewNavEnabled() ? 'rgb(202, 232, 240)' : '',
                cursor: 'pointer',
              },
              padding: '0',
            }}
            w="100%"
            ref={buttonRef}
          >
            <MenuListContent className="notifications-btn" icon={Notifications} displayName="Notifications" />
            {Boolean(notificationsCount) && (
              <NotificationBadge notificationCount={notificationsCount} transform="translate(36px, -8px)" />
            )}{' '}
            {checkNewNavEnabled() ? null : <ChevronRight color="text.g2" ml="auto" boxSize="24px" />}
          </Flex>
        </PopoverTrigger>
        {checkNewNavEnabled() ? (
          <StyledPopper maxWidth={maxWidth} width={checkNewNavEnabled() ? '100%' : ''}>
            <>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                alignSelf="stretch"
                borderRadius="8px"
                position="fixed"
                background="white"
                zIndex="101" // 100 is the index for the infinite notification loader
                paddingBottom="12px"
                left="8px"
                right="8px"
              >
                <Box
                  display="flex"
                  padding="16px 12px 16px 12px"
                  alignItems="center"
                  gap="8px"
                  alignSelf="stretch"
                  borderBottom="1px solid #CFD7DF"
                >
                  <TextBodyLarge bold>Notifications</TextBodyLarge>
                </Box>
              </Box>
              <StyledPaper elevation={0} ref={panelRef} paddingTop="8px">
                {!meta.loading && notificationsData.length > 0 && (
                  <InfiniteScroll
                    initialLoad={false}
                    pageStart={1}
                    hasMore={meta.hasMore}
                    loadMore={fetchPaginatedNotifications}
                    threshold={100}
                    useWindow={false}
                    loader={<Skeleton key="notificationLoader" height="50px" />}
                  >
                    <StyledMenuList disablePortal id="notificationsSection" marginTop="57px ">
                      {notificationsData.map((notificationData, index) => {
                        const updatedNotificationData = {
                          ...notificationData,
                          text: findAndReplaceString(
                            notificationData.text,
                            modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                          ),
                        };
                        return (
                          <NotificationComponent
                            key={`notificationsSection-item-${updatedNotificationData.id || index}`}
                            updateIsClicked={updateNotificationClickStatus}
                            closePopup={onClose}
                            data={updatedNotificationData}
                            showDetails={showDetails}
                            closeMenu={() => {
                              onNotificationClick();
                              closeMenu();
                              onClose();
                            }}
                          />
                        );
                      })}
                    </StyledMenuList>
                  </InfiniteScroll>
                )}
                {!meta.loading && notificationsData.length <= 0 && <NoNotificationsView key="noNotificationView" />}
                {meta.loading && <NoDataComponent message="Loading your notifications" />}
              </StyledPaper>
            </>
          </StyledPopper>
        ) : (
          <StyledPopper maxWidth={maxWidth}>
            <StyledPaper elevation={0} ref={panelRef}>
              {!meta.loading && notificationsData.length > 0 && (
                <InfiniteScroll
                  initialLoad={false}
                  pageStart={1}
                  hasMore={meta.hasMore}
                  loadMore={fetchPaginatedNotifications}
                  useWindow={false}
                  loader={<Skeleton key="notificationLoader" height="50px" />}
                >
                  <StyledMenuList disablePortal id="notificationsSection">
                    {notificationsData.map((notificationData, index) => {
                      const updatedNotificationData = {
                        ...notificationData,
                        text: findAndReplaceString(
                          notificationData.text,
                          modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                        ),
                      };
                      return (
                        <NotificationComponent
                          key={`notificationsSection-item-${updatedNotificationData.id || index}`}
                          updateIsClicked={updateNotificationClickStatus}
                          closePopup={onClose}
                          data={updatedNotificationData}
                          showDetails={showDetails}
                          closeMenu={() => {
                            closeMenu();
                            onClose();
                          }}
                        />
                      );
                    })}
                  </StyledMenuList>
                </InfiniteScroll>
              )}
              {!meta.loading && notificationsData.length <= 0 && <NoNotificationsView key="noNotificationView" />}
              {meta.loading && <NoDataComponent message="Loading your notifications" />}
            </StyledPaper>
          </StyledPopper>
        )}
      </>
    </Popover>
  );
};

const StyledPopper = styled(PopoverContent)`
  max-width: ${(props) => props.maxWidth};
  width: ${(props) => props.width};
  li {
    white-space: inherit;
    display: block;
  }
  z-index: 9;
  border-radius: 8px;
`;

const StyledMenuList = styled(MenuList)`
  padding: 0;
  height: 100%;
  overflow: auto;
  margin-top: ${(props) => props.marginTop};
`;

const StyledPaper = styled(Paper)`
  max-height: 50vh;
  overflow-x: hidden;
  z-index: 100;
  padding: 8px;
  padding-top: 0;
  padding-top: ${(props) => props.paddingTop};
  width: 390px;
  border-radius: 8px;
  ${HideScrollbarCSS}
  &::-webkit-scrollbar {
    display: block !important;
    width: 0 !important;
  }
  box-shadow: 0px 2px 8px 0px #00000014;
`;

export default NotificationsPanel;
